import { useToast } from '@ais-ui/ui-core';
import { useForm } from 'react-hook-form';
import {
  convertPhysicalAtonValuesToBackend,
  mutatePhysicalAtonFormSchema,
  MutatePhysicalAtonFormValues,
} from './atonForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';
import { AtoNWithDate, useAddAton, useUpdateAton } from '@ais-ui/api';
import { useTargetChooser } from '../../Map/contexts';
import { AtonTargetInfo, TargetInfoType } from '@ais-ui/websockets';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { AtoN } from '@ais-ui/api-spec/config';

export const usePhysicalAtonMutation = (
  setIsSubmitting: Dispatch<SetStateAction<boolean>>,
  setShowDialog: Dispatch<SetStateAction<boolean>>,
  atonObj: AtoNWithDate | null,
  setAton: Dispatch<SetStateAction<AtoNWithDate | null>>,
) => {
  const { toast } = useToast();

  const {
    setShowChooseTargetPanel,
    onChooseTarget,
    onRejectTarget,
    setChooseTargetTypes,
  } = useTargetChooser();

  const { messages } = useLocalization();
  const { mutate: createAton } = useAddAton();
  const { mutate: updateAton } = useUpdateAton();
  const physicalForm = useForm<MutatePhysicalAtonFormValues>({
    resolver: zodResolver(mutatePhysicalAtonFormSchema),
    defaultValues: {
      name: '',
      description: '',
      warningTimeout: '00:07:00',
      failureTimeout: '00:13:00',
      email: [],
      phones: [],
      selectedGroups: [],
    },
  });

  const onCreatePhysical = async (data: MutatePhysicalAtonFormValues) => {
    const backendValues = convertPhysicalAtonValuesToBackend(data);

    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'create',
      'aton',
      setIsSubmitting,
      setShowDialog,
    );

    createAton(backendValues as AtoN, callBacks);
    physicalForm.reset();
  };

  const onUpdatePhysical = async (data: MutatePhysicalAtonFormValues) => {
    if (!atonObj) {
      toast({
        title: messages.genericToastTitleError,
        description: "Couldn't find AtoN to update",
      });
      return;
    }

    const backendValues = convertPhysicalAtonValuesToBackend(
      data,
      atonObj.alarmSetup,
    );

    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'edit',
      'aton',
      setIsSubmitting,
      setShowDialog,
    );

    updateAton({ id: atonObj.uuid as string, aton: backendValues }, callBacks);
    setAton(null);
  };

  // Stabilised with useCallback: this is a dependency of the memoised AtoN
  // section config. If it got a fresh identity every render, that config would
  // rebuild and remount the active pane on every keystroke, blurring inputs.
  const onSelectAtonOnMap = useCallback(() => {
    setShowDialog(false);
    setShowChooseTargetPanel(true);
    setChooseTargetTypes(['aton']);

    onChooseTarget.current = (target: TargetInfoType) => {
      const selectedAton = target as AtonTargetInfo;
      physicalForm.setValue('name', selectedAton.name);
      physicalForm.setValue('mmsi', parseInt(selectedAton.mmsi));
      physicalForm.setValue('latitude', selectedAton.latitude);
      physicalForm.setValue('longitude', selectedAton.longitude);

      setShowChooseTargetPanel(false);
      setShowDialog(true);
    };

    onRejectTarget.current = () => {
      setShowDialog(true);
      setShowChooseTargetPanel(false);
    };
  }, [
    setShowDialog,
    setShowChooseTargetPanel,
    setChooseTargetTypes,
    onChooseTarget,
    onRejectTarget,
    physicalForm,
  ]);

  return {
    physicalForm,
    onCreatePhysical,
    onUpdatePhysical,
    onSelectAtonOnMap,
  };
};
